import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// FlockTrade has two PWA manifests on the same origin:
//   - /manifest.json          (site-wide, scope "/", existing)
//   - /manifest-terminal.json (terminal-only, scope "/terminal")
//
// This component swaps the <link rel="manifest"> href at runtime so the
// correct manifest is active for the route the user is on. Browsers pick
// up the manifest via the DOM link element at install-prompt time, so
// changing href before any engagement heuristics fire is enough — no
// restart or reload required.
//
// Why DOM manipulation instead of react-helmet: the static index.html
// already ships a <link rel="manifest">, and react-helmet appends rather
// than replaces. Stacking two manifest links in <head> has undefined
// browser behavior. A direct setAttribute on the existing element is
// atomic and deterministic.
//
// Defensive fallback: create the link if it is missing (future refactor
// of index.html should not break this component).
export default function PWAManifestManager() {
  const location = useLocation();

  // Only pathname — query and hash must not trigger a manifest swap.
  // Workspace navigation inside /terminal (e.g. ?workspace=X) keeps the
  // terminal manifest; route transitions in and out of /terminal swap it.
  useEffect(() => {
    const manifestPath = location.pathname.startsWith('/terminal')
      ? '/manifest-terminal.json'
      : '/manifest.json';

    let link = document.querySelector('link[rel="manifest"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'manifest';
      document.head.appendChild(link);
    }
    if (link.getAttribute('href') !== manifestPath) {
      link.setAttribute('href', manifestPath);
    }
  }, [location.pathname]);

  return null;
}
